import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { SpotClient } from '@frontend/spot/api';
import { Spot } from '@frontend/spot/types';
import { TransactionClient } from '@frontend/transaction/api';
import { Transaction, TransactionStateName } from '@frontend/transaction/types';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { isMovableTransactionState } from '../../hooks/useMoveTransaction';
import { useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';

export interface MoveSelectSpotViewProps {
    spots: Spot[];
    selectedSpotId: string | null;
    setSelectedSpotId: (spotId: string) => void;
    isLoading: boolean;
    error: ReactNode | null;
    canContinue: boolean;
    handleContinue: () => void;
    handleBack: () => void;
}

export const useMoveSelectSpot = (): MoveSelectSpotViewProps => {
    const { slotId, transactionId } = useParams<{ slotId: string; transactionId: string }>();
    const navigate = useKioskNavigate();
    const navigationState = useAppSelector((state) => state.navigation);
    const userAuth = navigationState.user?.auth;
    const currentSpotId = navigationState.spot?.id ?? null;

    const [spots, setSpots] = useState<Spot[]>([]);
    const [selectedSpotId, setSelectedSpotId] = useState<string | null>(currentSpotId);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<ReactNode | null>(null);
    const [transaction, setTransaction] = useState<Transaction | null>(null);

    useEffect(() => {
        const accountId = navigationState.user?.accountId ?? navigationState.iot?.account_id;
        if (!transactionId || !accountId || !userAuth) {
            setIsLoading(false);
            setError(CommonMessage.ERRORS.DEFAULT);
            return;
        }

        setIsLoading(true);
        setError(null);

        Promise.all([
            TransactionClient.fetchTransaction(accountId, transactionId),
            SpotClient.fetchSpots({ sort_field: 'name', sort_direction: 'asc', size: '200', index: '0' }, userAuth)
        ])
            .then(([trx, spotsResponse]) => {
                if (!isMovableTransactionState(trx.state as TransactionStateName)) {
                    setError(CommonMessage.ERRORS.DEFAULT);
                    setTransaction(null);
                    setSpots([]);
                    return;
                }
                setTransaction(trx);
                const results = spotsResponse.results ?? [];
                setSpots(results);
                const preferredSpotId = trx.spot_id ?? currentSpotId;
                if (preferredSpotId && results.some((spot) => spot.id === preferredSpotId)) {
                    setSelectedSpotId(preferredSpotId);
                } else if (results.length > 0) {
                    setSelectedSpotId(results[0].id);
                }
            })
            .catch((err) => {
                Logger.error('Failed to load move transaction spot selection', err);
                setError(CommonMessage.ERRORS.FAILED_TO_LOAD);
            })
            .finally(() => setIsLoading(false));
    }, [transactionId, navigationState.user?.accountId, navigationState.iot?.account_id, userAuth, currentSpotId]);

    const handleContinue = useCallback(() => {
        if (!slotId || !transactionId || !selectedSpotId || !transaction) return;
        navigate(`/spots/layout/slot/${slotId}/move/${transactionId}/spot/${selectedSpotId}`);
    }, [navigate, slotId, transactionId, selectedSpotId, transaction]);

    const handleBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        spots,
        selectedSpotId,
        setSelectedSpotId,
        isLoading,
        error,
        canContinue: !!selectedSpotId && !!transaction && !error,
        handleContinue,
        handleBack
    };
};
